import {useState, type FC} from "react";
import {Controller, useForm} from "react-hook-form";
import {joiResolver} from "@hookform/resolvers/joi";
import {useMutation} from "@tanstack/react-query";
import {useNavigate} from "react-router-dom";
import {Captcha} from "@src/components/captcha";

import {Button} from "@src/components";
import {registrationSchema} from "@src/core/schemas";
import {
    Back,
    CheckBoxContainer,
    Container,
    ContentContainer,
    HelperText,
    PageTitle,
    RelativeContainer,
    StyledTextField,
    TextLink,
    palette, ALink, StyledLink,
    ContainerSpaceBetween,
    SocialAuthGroup,
    SocialAuthTitle,
    SocialAuthTitleText,
    Line,
    SocialButtonGroup,
    VkButton,
    YandexButton,
    GoogleButton
} from "@src/core/styles";
import type {
    RegistrationErrorResponse,
    RegistrationFormValues,
} from "@src/core/types";
import {CheckBoxWrapper, FieldGroup} from "../login-page/login-page.styles";
import {
    CheckBoxGroup,
    FieldGroupRow,
    FieldGroupWrapper,
    SocialAuthRegistration,
    RegistartionForm
} from "./registration-page.styles";
import {Authentication} from "@src/core/services";
import {ReactComponent as Arrow} from "@src/core/icons/arrow.svg";
import {ReactComponent as Check} from "@src/core/icons/check.svg";
import {Checkbox, FormControlLabel} from "@mui/material";
import {AxiosError} from "axios";
import {RegistrationErrors} from "./registration-page.types";
import {FormContainer} from "../route-management-page/components/form-component/form-components.styles";
import {APP_ROUTES} from "@src/core/constants";
import {ReactComponent as Vk} from "@src/core/icons/vk.svg";
import {ReactComponent as Yandex} from "@src/core/icons/yandex.svg";
import {ReactComponent as Google} from "@src/core/icons/google.svg";
import {ReactComponent as Ok} from "@src/core/icons/ok.svg";
import {Link} from "react-router-dom";

export const Registration: FC = () => {
    const navigate = useNavigate();
    const [errorsRequest, setErrorsRequest] = useState<RegistrationErrors | undefined>();
    const [captchaState, setCaptchaState] = useState<boolean>(false);

    const captchaValidation = (value: any) => {
        setCaptchaState(value);

        return value;
    }

    const {
        handleSubmit,
        control,
        formState: {errors, isValid},
    } = useForm<RegistrationFormValues>({
        mode: "onChange",
        resolver: joiResolver(registrationSchema),
        defaultValues: {
            first_name: "",
            last_name: "",
            login: "",
            password: "",
            password2: "",
            email: "",
            userAgreement: false,
            personalData: false,
        },
    });

    const fullValidation = isValid && captchaState;

    const {mutate} = useMutation(Authentication.registration, {
        onSuccess: (response) => {
            ym(89673753, 'reachGoal', 'btn-click-me');
            // код в случае успешного запроса
            navigate(APP_ROUTES.LOGIN);
        },
        onError: (error: AxiosError<RegistrationErrorResponse>) => {
            setErrorsRequest(error.response?.data.errors);
        },
    });

    const backHandler = () => {
        navigate(-1);
    };

    const onSubmit = (data: RegistrationFormValues) => {
        const {userAgreement, personalData, ...reqParams} = data;
        mutate(reqParams);
    };

    const changeHandler = () => {
        setErrorsRequest(undefined);
    };

    const socialHandle = (type: string) => {
        return window.location.replace('/social-auth/'+type)
    }

    const socialHandleVK = () => {
        return window.location.replace('/social-auth/vkontakte');
    }

    const socialHandleYandex = () => {
        return window.location.replace('/social-auth/yandex');
    }

    const socialHandleGoogle = () => {
        return window.location.replace('/social-auth/google');
    }

    return (
        <Container>
            <ContentContainer>
                <FormContainer>
                    <Back onClick={backHandler}>
                        <Arrow/>
                    </Back>
                    <PageTitle>Регистрация</PageTitle>
                    <ContainerSpaceBetween>
                        <RegistartionForm>
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <FieldGroupWrapper>
                                    <FieldGroup>
                                        <FieldGroupRow gap={24}>
                                            <Controller
                                                control={control}
                                                name="first_name"
                                                render={({field: {value, onBlur, onChange}}) => (
                                                    <StyledTextField
                                                        onBlur={onBlur}
                                                        variant="outlined"
                                                        label="Имя"
                                                        autoComplete="on"
                                                        InputLabelProps={{
                                                            sx: {
                                                                "&.Mui-focused": {
                                                                    color: `${palette.colors.green_base}`,
                                                                },
                                                                "&.Mui-error": {
                                                                    color: `${palette.colors.error}`,
                                                                },
                                                            },
                                                        }}
                                                        value={value}
                                                        error={
                                                            !!errors?.first_name?.message ??
                                                            errorsRequest?.first_name
                                                        }
                                                        helperText={
                                                            errors?.first_name?.message ||
                                                            errorsRequest?.first_name
                                                        }
                                                        onChange={(e) => {
                                                            changeHandler();
                                                            onChange(e);
                                                        }}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                control={control}
                                                name="last_name"
                                                render={({field: {value, onBlur, onChange}}) => (
                                                    <StyledTextField
                                                        onBlur={onBlur}
                                                        variant="outlined"
                                                        label="Фамилия"
                                                        autoComplete="on"
                                                        InputLabelProps={{
                                                            sx: {
                                                                "&.Mui-focused": {
                                                                    color: `${palette.colors.green_base}`,
                                                                },
                                                                "&.Mui-error": {
                                                                    color: `${palette.colors.error}`,
                                                                },
                                                            },
                                                        }}
                                                        value={value}
                                                        error={
                                                            !!errors?.last_name?.message ??
                                                            !!errorsRequest?.last_name
                                                        }
                                                        helperText={
                                                            errors?.last_name?.message || errorsRequest?.last_name
                                                        }
                                                        onChange={(e) => {
                                                            changeHandler();
                                                            onChange(e);
                                                        }}
                                                    />
                                                )}
                                            />
                                        </FieldGroupRow>
                                        <RelativeContainer>
                                            <Controller
                                                control={control}
                                                name="login"
                                                render={({field: {value, onBlur, onChange}}) => (
                                                    <StyledTextField
                                                        onBlur={onBlur}
                                                        variant="outlined"
                                                        label="Логин"
                                                        autoComplete="on"
                                                        InputLabelProps={{
                                                            sx: {
                                                                "&.Mui-focused": {
                                                                    color: `${palette.colors.green_base}`,
                                                                },
                                                                "&.Mui-error": {
                                                                    color: `${palette.colors.error}`,
                                                                },
                                                            },
                                                        }}
                                                        value={value}
                                                        error={
                                                            !!errors?.login?.message || !!errorsRequest?.login
                                                        }
                                                        helperText={
                                                            errors?.login?.message || errorsRequest?.login
                                                        }
                                                        onChange={(e) => {
                                                            changeHandler();
                                                            onChange(e);
                                                        }}
                                                    />
                                                )}
                                            />
                                            <HelperText>Нужен для входа</HelperText>
                                        </RelativeContainer>
                                        <Controller
                                            control={control}
                                            name="password"
                                            render={({field: {value, onBlur, onChange}}) => (
                                                <StyledTextField
                                                    onBlur={onBlur}
                                                    variant="outlined"
                                                    type="password"
                                                    label="Пароль"
                                                    InputLabelProps={{
                                                        sx: {
                                                            "&.Mui-focused": {
                                                                color: `${palette.colors.green_base}`,
                                                            },
                                                            "&.Mui-error": {color: `${palette.colors.error}`},
                                                        },
                                                    }}
                                                    autoComplete="off"
                                                    value={value}
                                                    error={
                                                        !!errors?.password?.message || !!errorsRequest?.password
                                                    }
                                                    helperText={
                                                        errors?.password?.message || errorsRequest?.password
                                                    }
                                                    onChange={(e) => {
                                                        changeHandler();
                                                        onChange(e);
                                                    }}
                                                />
                                            )}
                                        />
                                        <Controller
                                            control={control}
                                            name="password2"
                                            render={({field: {value, onBlur, onChange}}) => (
                                                <StyledTextField
                                                    onBlur={onBlur}
                                                    variant="outlined"
                                                    type="password"
                                                    label="Подтверждение нового пароля"
                                                    InputLabelProps={{
                                                        sx: {
                                                            "&.Mui-focused": {
                                                                color: `${palette.colors.green_base}`,
                                                            },
                                                            "&.Mui-error": {color: `${palette.colors.error}`},
                                                        },
                                                    }}
                                                    autoComplete="off"
                                                    value={value}
                                                    error={!!errors?.password2?.message}
                                                    helperText={errors?.password2?.message}
                                                    onChange={(e) => {
                                                        changeHandler();
                                                        onChange(e);
                                                    }}
                                                />
                                            )}
                                        />
                                        <Controller
                                            control={control}
                                            name="email"
                                            render={({field: {value, onBlur, onChange}}) => (
                                                <StyledTextField
                                                    onBlur={onBlur}
                                                    variant="outlined"
                                                    label="Электронная почта"
                                                    placeholder="Электронная почта"
                                                    autoComplete="on"
                                                    InputLabelProps={{
                                                        sx: {
                                                            "&.Mui-focused": {
                                                                color: `${palette.colors.green_base}`,
                                                            },
                                                            "&.Mui-error": {color: `${palette.colors.error}`},
                                                        },
                                                    }}
                                                    value={value}
                                                    error={!!errors?.email?.message ?? !!errorsRequest?.email}
                                                    helperText={
                                                        errors?.email?.message || errorsRequest?.email
                                                    }
                                                    onChange={(e) => {
                                                        changeHandler();
                                                        onChange(e);
                                                    }}
                                                />
                                            )}
                                        />
                                    </FieldGroup>
                                </FieldGroupWrapper>

                                <CheckBoxGroup>
                                    <CheckBoxWrapper>
                                        <Controller
                                            name="userAgreement"
                                            control={control}
                                            render={({field: {onChange, value}}) => (
                                                <CheckBoxContainer>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox checked={value} onChange={onChange}/>
                                                        }
                                                        label={
                                                            <div>
                                                                Согласен с&nbsp;
                                                                <ALink>
                                                                    <a href={"/file/term_of_use.pdf"} target="_blank">Пользовательским
                                                                        соглашением</a>
                                                                </ALink>
                                                            </div>
                                                        }
                                                    />
                                                </CheckBoxContainer>
                                            )}
                                        />
                                    </CheckBoxWrapper>
                                    <CheckBoxWrapper>
                                        <Controller
                                            name="personalData"
                                            control={control}
                                            render={({field: {onChange, value}}) => (
                                                <CheckBoxContainer>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox checked={value} onChange={onChange}/>
                                                        }
                                                        label={
                                                            <div>
                                                                Даю &nbsp;
                                                                <ALink><a href="/file/agree_processing_of_p_d.pdf"
                                                                          target="_blank">согласие на обработку
                                                                    персональных данных</a></ALink>
                                                                &nbsp; и соглашаюсь с <ALink><a
                                                                href="/file/p_d_processing_policy.pdf" target="_blank">Политикой
                                                                обработки персональных данных</a></ALink>
                                                            </div>
                                                        }
                                                    />
                                                </CheckBoxContainer>
                                            )}
                                        />
                                    </CheckBoxWrapper>
                                </CheckBoxGroup>

                                <Captcha CaptchaState={captchaValidation}/>
                                <Button
                                    text="Зарегистрироваться"
                                    type="submit"
                                    icon={<Check/>}
                                    borderradius="L"
                                    variant="contained"
                                    disabled={!fullValidation}
                                />
                            </form>
                        </RegistartionForm>
                        <SocialAuthRegistration>
                            <SocialAuthGroup>
                                <SocialAuthTitle>
                                    <Line/>
                                    <SocialAuthTitleText>Вход&nbsp;через</SocialAuthTitleText>
                                    <Line/>
                                </SocialAuthTitle>
                                <SocialButtonGroup>
                                    {/* VK ID временно отключен: авторизация не работает, пока не настроен доступ к email в кабинете VK ID */}
                                    {/*<Link to="#" onClick={socialHandleVK}>*/}
                                    {/*    <VkButton>*/}
                                    {/*        <Vk/>*/}
                                    {/*        VK ID*/}
                                    {/*    </VkButton>*/}
                                    {/*</Link>*/}
                                    <Link to="#" onClick={socialHandleYandex}>
                                        <YandexButton>
                                            <Yandex/>
                                            Яндекс ID
                                        </YandexButton>
                                    </Link>
                                    {/*<Link to="#" onClick={socialHandleGoogle}>*/}
                                    {/*    <GoogleButton>*/}
                                    {/*        <Google/>*/}
                                    {/*        Google*/}
                                    {/*    </GoogleButton>*/}
                                    {/*</Link>*/}
                                </SocialButtonGroup>
                            </SocialAuthGroup>
                        </SocialAuthRegistration>
                    </ContainerSpaceBetween>
                </FormContainer>
            </ContentContainer>
        </Container>
    );
};
